import { useEffect, useState } from 'react';
import { api, fmtMoney } from '../lib/api.js';
import SubsegmentPicker from './SubsegmentPicker.jsx';

const PAYMENT_METHODS = ['cash', 'check', 'venmo', 'zelle', 'square', 'card', 'personal_card', 'other'];

/**
 * Modal that loads a single expense or income row and lets the user edit it.
 * Submits a PATCH; offers Delete for owners.
 */
export default function TransactionEditor({ type, id, isOwner, onClose }) {
  const [data, setData] = useState(null);
  const [segments, setSegments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/segments'),
      api.get('/api/schedule-f'),
      type === 'expense'
        ? api.get(`/api/receipts/${id}`)
        : api.get('/api/income').then(r => ({
            // The income list endpoint returns an array; find the one we want.
            // (No /api/income/:id getter today — list-then-pick is fine for V1.)
            income: r.income.find(x => x.id === id),
          })),
    ]).then(([s, c, r]) => {
      setSegments(s.segments || []);
      setCategories(c.categories || []);
      if (type === 'expense') {
        setData({ kind: 'expense', header: r.receipt, lines: r.lines });
      } else {
        setData({ kind: 'income', row: r.income });
      }
    }).catch(e => setErr(e.message));
  }, [type, id]);

  if (err) return <Modal onClose={onClose}><div className="text-rust-700">{err}</div></Modal>;
  if (!data) return <Modal onClose={onClose}><div className="text-ink-700/60 italic">Loading…</div></Modal>;

  if (data.kind === 'expense') {
    return <ExpenseEditor data={data} segments={segments} categories={categories.filter(c => c.side === 'expense')} isOwner={isOwner} onClose={onClose} setBusy={setBusy} setErr={setErr} busy={busy} err={err} />;
  }
  return <IncomeEditor data={data} segments={segments} categories={categories.filter(c => c.side === 'income')} isOwner={isOwner} onClose={onClose} setBusy={setBusy} setErr={setErr} busy={busy} err={err} />;
}

function ExpenseEditor({ data, segments, categories, isOwner, onClose, busy, setBusy, err, setErr }) {
  const e = data.header;
  const [form, setForm] = useState({
    vendor: e.vendor || '',
    date: e.date || '',
    payment_method: e.payment_method || 'card',
    ms_farmer_tax_flag: !!e.ms_farmer_tax_flag,
    no_receipt: !!e.no_receipt,
    paid_by_personal_card: e.paid_by_user_id != null,
  });
  const [lines, setLines] = useState((data.lines || []).map(l => ({
    description: l.description || '',
    qty: l.qty ?? 1,
    unit_price_dollars: l.unit_price_cents != null ? (l.unit_price_cents / 100).toFixed(2) : '',
    line_total_cents: l.line_total_cents,
    segment_id: l.segment_id || '',
    schedule_f_code: l.schedule_f_code || '',
    chicken_batch_id: l.chicken_batch_id ?? null,
    cattle_id:        l.cattle_id        ?? null,
    is_capex: !!l.is_capex,
  })));

  function setLine(i, k, v) { setLines(arr => arr.map((l, j) => j === i ? { ...l, [k]: v } : l)); }
  function patchLine(i, patch) { setLines(arr => arr.map((l, j) => j === i ? { ...l, ...patch } : l)); }
  function addLine() { setLines(arr => [...arr, { description: '', qty: 1, unit_price_dollars: '', line_total_cents: 0, segment_id: '', schedule_f_code: '', chicken_batch_id: null, cattle_id: null }]); }
  function removeLine(i) { setLines(arr => arr.filter((_, j) => j !== i)); }

  async function save() {
    setBusy(true); setErr('');
    try {
      const payload = {
        vendor: form.vendor.trim(),
        date: form.date,
        payment_method: form.payment_method,
        ms_farmer_tax_flag: form.ms_farmer_tax_flag,
        no_receipt: form.no_receipt,
        paid_by_user_id: form.paid_by_personal_card ? (e.paid_by_user_id || e.submitted_by) : null,
        lines: lines.map(l => {
          const unit = Math.round(Number(l.unit_price_dollars || 0) * 100);
          const qty = Number(l.qty || 1);
          return {
            description: l.description.trim() || null,
            qty,
            unit_price_cents: unit,
            line_total_cents: Math.round(unit * qty),
            segment_id: l.segment_id || null,
            schedule_f_code: l.schedule_f_code || null,
            chicken_batch_id: l.chicken_batch_id ?? null,
            cattle_id:        l.cattle_id        ?? null,
            is_capex:         !!l.is_capex,
          };
        }).filter(l => l.line_total_cents > 0),
      };
      await api.patch(`/api/expenses/${e.id}`, payload);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function del() {
    if (!confirm('Delete this receipt? Cannot be undone.')) return;
    setBusy(true);
    try { await api.delete(`/api/expenses/${e.id}`); onClose(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  const total = lines.reduce((s, l) => s + Math.round(Number(l.unit_price_dollars || 0) * 100 * Number(l.qty || 0)), 0);

  return (
    <Modal onClose={onClose} wide>
      <div className="space-y-4">
        <h2 className="text-xl font-serif text-olive-900">Edit receipt</h2>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Vendor</label>
            <input className="input" value={form.vendor} onChange={ev => setForm(f => ({ ...f, vendor: ev.target.value }))} />
          </div>
          <div>
            <label className="label">Date</label>
            <input className="input" type="date" value={form.date} onChange={ev => setForm(f => ({ ...f, date: ev.target.value }))} />
          </div>
          <div>
            <label className="label">Payment</label>
            <select className="input" value={form.payment_method} onChange={ev => setForm(f => ({ ...f, payment_method: ev.target.value }))}>
              {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.no_receipt} onChange={ev => setForm(f => ({ ...f, no_receipt: ev.target.checked }))} />
              No paper receipt
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-sm col-span-2">
            <input type="checkbox" checked={form.ms_farmer_tax_flag} onChange={ev => setForm(f => ({ ...f, ms_farmer_tax_flag: ev.target.checked }))} />
            MS Commercial Farmer 1.5%
          </label>
          <label className="inline-flex items-center gap-2 text-sm col-span-2">
            <input type="checkbox" checked={form.paid_by_personal_card} onChange={ev => setForm(f => ({ ...f, paid_by_personal_card: ev.target.checked }))} />
            Personal card (reimbursable)
          </label>
        </div>

        <div className="border-t border-olive-100 pt-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-olive-900">Line items</h3>
            <button type="button" onClick={addLine} className="btn-ghost text-sm">+ Add line</button>
          </div>
          <div className="space-y-2 mt-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <label className="label">Description</label>
                  <input className="input" value={l.description} onChange={ev => setLine(i, 'description', ev.target.value)} />
                </div>
                <div className="col-span-1">
                  <label className="label">Qty</label>
                  <input className="input" type="number" step="0.01" value={l.qty} onChange={ev => setLine(i, 'qty', ev.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label">Unit $</label>
                  <input className="input" type="number" step="0.01" value={l.unit_price_dollars} onChange={ev => setLine(i, 'unit_price_dollars', ev.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label">Segment</label>
                  <select className="input" value={l.segment_id} onChange={ev => setLine(i, 'segment_id', ev.target.value)}>
                    <option value="">—</option>
                    {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label">Schedule F</label>
                  <select className="input" value={l.schedule_f_code} onChange={ev => setLine(i, 'schedule_f_code', ev.target.value)}>
                    <option value="">—</option>
                    {categories.map(c => <option key={c.code} value={c.code}>L{c.schedule_f_line} · {c.description.slice(0, 18)}</option>)}
                  </select>
                </div>
                <div className="col-span-1 text-right">
                  <button type="button" onClick={() => removeLine(i)} className="text-xs text-rust-700 hover:underline">×</button>
                </div>
                <div className="col-span-12 -mt-1 pl-1">
                  <label className="inline-flex items-center gap-2 text-xs text-ink-700/80">
                    <input
                      type="checkbox"
                      checked={!!l.is_capex}
                      onChange={ev => setLine(i, 'is_capex', ev.target.checked)}
                    />
                    Capital expense (depreciate / add to CapEx ledger) — excluded from segment OpEx
                  </label>
                </div>
                {(l.segment_id === 'meat_chickens' || l.segment_id === 'egg_chickens' || l.segment_id === 'cattle') && (
                  <div className="col-span-12 -mt-1 pl-1">
                    <SubsegmentPicker
                      segmentId={l.segment_id}
                      value={l.chicken_batch_id ?? l.cattle_id}
                      onChange={(patch) => patchLine(i, { chicken_batch_id: null, cattle_id: null, ...patch })}
                      compact
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-right text-lg font-semibold text-olive-900 mt-2">{fmtMoney(total)}</div>
        </div>

        {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

        <div className="flex gap-2 justify-between items-center">
          {isOwner && <button type="button" onClick={del} disabled={busy} className="text-xs text-rust-700 hover:underline">Delete receipt</button>}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="btn-ghost">Close</button>
            <button onClick={save} className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function IncomeEditor({ data, segments, categories, isOwner, onClose, busy, setBusy, err, setErr }) {
  const i = data.row;
  if (!i) {
    return <Modal onClose={onClose}><div className="text-rust-700">Income row not found.</div></Modal>;
  }
  const [form, setForm] = useState({
    customer_name: i.customer_name || '',
    date: i.date || '',
    description: i.description || '',
    total_dollars: i.total_cents != null ? (i.total_cents / 100).toFixed(2) : '',
    paid_dollars: i.paid_cents != null ? (i.paid_cents / 100).toFixed(2) : '',
    payment_method: i.payment_method || 'cash',
    segment_id: i.segment_id || '',
    schedule_f_code: i.schedule_f_code || '',
    chicken_batch_id: i.chicken_batch_id ?? null,
    cattle_id:        i.cattle_id        ?? null,
  });

  async function save() {
    setBusy(true); setErr('');
    try {
      await api.patch(`/api/income/${i.id}`, {
        customer_name: form.customer_name.trim() || null,
        date: form.date,
        description: form.description.trim() || null,
        total_cents: Math.round(Number(form.total_dollars || 0) * 100),
        paid_cents: Math.round(Number(form.paid_dollars || 0) * 100),
        payment_method: form.payment_method,
        segment_id: form.segment_id || null,
        schedule_f_code: form.schedule_f_code || null,
        chicken_batch_id: form.chicken_batch_id,
        cattle_id: form.cattle_id,
      });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function del() {
    if (!confirm('Delete this income record? Cannot be undone.')) return;
    setBusy(true);
    try { await api.delete(`/api/income/${i.id}`); onClose(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <Modal onClose={onClose}>
      <div className="space-y-4">
        <h2 className="text-xl font-serif text-olive-900">Edit income</h2>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label">Customer</label>
            <input className="input" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <input className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="label">Total $</label>
            <input className="input" type="number" step="0.01" value={form.total_dollars} onChange={e => setForm(f => ({ ...f, total_dollars: e.target.value }))} />
          </div>
          <div>
            <label className="label">Paid $</label>
            <input className="input" type="number" step="0.01" value={form.paid_dollars} onChange={e => setForm(f => ({ ...f, paid_dollars: e.target.value }))} />
          </div>
          <div>
            <label className="label">Date</label>
            <input className="input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div>
            <label className="label">Payment</label>
            <select className="input" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
              {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Segment</label>
            <select className="input" value={form.segment_id} onChange={e => setForm(f => ({ ...f, segment_id: e.target.value, chicken_batch_id: null, cattle_id: null }))}>
              <option value="">—</option>
              {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Schedule F</label>
            <select className="input" value={form.schedule_f_code} onChange={e => setForm(f => ({ ...f, schedule_f_code: e.target.value }))}>
              <option value="">—</option>
              {categories.map(c => <option key={c.code} value={c.code}>L{c.schedule_f_line} · {c.description.slice(0, 22)}</option>)}
            </select>
          </div>
          <SubsegmentPicker
            segmentId={form.segment_id}
            value={form.chicken_batch_id ?? form.cattle_id}
            onChange={(patch) => setForm(f => ({ ...f, chicken_batch_id: null, cattle_id: null, ...patch }))}
          />
        </div>

        {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

        <div className="flex gap-2 justify-between items-center">
          {isOwner && <button type="button" onClick={del} disabled={busy} className="text-xs text-rust-700 hover:underline">Delete income</button>}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="btn-ghost">Close</button>
            <button onClick={save} className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 bg-ink-900/40 flex items-center justify-center p-4 z-30 overflow-y-auto" onClick={onClose}>
      <div className={`bg-cream-50 rounded-2xl p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} space-y-3 shadow-xl my-8`} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
